import { isValidElement } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import BaseCard from '../cards/BaseCard';
import {
	ArrowForwardIcon,
	Css,
	EmailIcon,
	ExternalLinkIcon,
	IContactCardProps,
	ITheme,
	Markdown,
	StyledCardPicture,
} from '../../../ui';
import { Color } from '../../../styles/Color';
import { ArrowLinkStyles } from '../button/ArrowLinkStyles';
import NoImage from '../../../assets/icons/no-image.svg';
import { RouterLink } from '../link/RouterLink';
import { IInternalLinkEntry } from '../../../contentful';
import LinkButton from '../button/LinkButton';
import { useCommonData } from '../../../common-data/useCommonData';
import { getUrlForPageId } from '../../../utils/getUrlForLink';
import { MarkdownRenderContextProvider } from '../markdown';

interface IContactItemProps extends IContactCardProps {
	type?: 'contact' | 'airline';
	subtitle?: string;
	isPortrait?: boolean;
	body?: string;
	links: {
		phoneNumber?: string;
		externalLink?: string;
		email?: string;
		internalLink?: IInternalLinkEntry;
	};
}

export default function ContactItem({
	type = 'contact',
	imageSource,
	imageAlt,
	title,
	linkTitle,
	subtitle,
	isPortrait,
	body,
	links,
}: IContactItemProps) {
	const { pathToPageIdMap } = useCommonData();
	const getLabelValue = () => linkTitle || 'Visit website';
	const websiteLabel = getLabelValue();
	const emailLabel = 'Email';
	const { phoneNumber, externalLink, email, internalLink } = links;
	const internalLinkPageId = internalLink?.fields.page?.fields.id;
	const hasThreeLinks = !!(email && externalLink && internalLink);

	const renderLink = (
		label: string,
		Icon: React.ElementType | null,
		href: string,
	) => (
		<CardLink
			href={href}
			target="_blank"
			aria-label={`${title} (opens in a new tab)`}
		>
			<span>{label}</span>
			{Icon && <Icon aria-hidden />}
		</CardLink>
	);

	const renderImage = () => {
		if (isPortrait && !imageSource)
			return (
				<NoImagePortraitWrapper>
					<NoImage />
				</NoImagePortraitWrapper>
			);

		if (!imageSource) return <NoImage />;

		// If imageSource is an SVG, render it directly
		if (isValidElement(imageSource)) {
			return imageSource;
		}

		return (
			<CardPictureStyled
				source={imageSource}
				alt={imageAlt}
				type={type}
				isPortrait={isPortrait}
			/>
		);
	};

	return (
		<BaseCard
			linkHref={externalLink}
			animationType="zoom"
			fullyClickable={false}
		>
			<CardPictureWrapper hasNoImage={!imageSource} isPortrait={isPortrait}>
				{renderImage()}
			</CardPictureWrapper>

			<CardContent type={type} isPortrait={isPortrait}>
				<div>
					<CardContentTitle
						aria-label={title}
						hasSubtitle={!!subtitle}
						isPortrait={isPortrait}
					>
						{title}
					</CardContentTitle>
					{subtitle && <CardContentSubtitle>{subtitle}</CardContentSubtitle>}

					{body && (
						<CardContentBody>
							<MarkdownRenderContextProvider>
								<Markdown source={body} />
							</MarkdownRenderContextProvider>
						</CardContentBody>
					)}
				</div>

				<ContactSection hasThreeLinks={hasThreeLinks}>
					{phoneNumber &&
						hasThreeLinks &&
						renderLink(phoneNumber, null, `tel:${phoneNumber}`)}

					<LinkRow>
						{phoneNumber &&
							!hasThreeLinks &&
							renderLink(phoneNumber, null, `tel:${phoneNumber}`)}

						{email && renderLink(emailLabel, EmailIcon, `mailto:${email}`)}

						{externalLink &&
							renderLink(websiteLabel, ExternalLinkIcon, externalLink)}

						{internalLink && internalLinkPageId && (
							<LinkButton
								href={getUrlForPageId(internalLinkPageId, pathToPageIdMap)}
								icon={<ArrowForwardIcon aria-hidden />}
								buttonType="text"
							>
								{internalLink.fields.title}
							</LinkButton>
						)}
					</LinkRow>
				</ContactSection>
			</CardContent>
		</BaseCard>
	);
}

const PORTRAIT_IMAGE_HEIGHT = '288px;';

const CardContent = styled.div<
	{ type: IContactItemProps['type']; isPortrait?: boolean },
	ITheme
>`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	flex: 1;
	padding: ${Css.pxToRem(12)} ${Css.pxToRem(16)} ${Css.pxToRem(20)};
	background-color: ${({ type }) =>
		type === 'contact' ? Color.Neutral2 : Color.Neutral1};
	border-radius: ${(props) => props.theme.card.borderRadius};
	border-top-left-radius: 0;
	border-top-right-radius: 0;
	border-top: 1px solid ${Color.BorderCard};

	${({ isPortrait }) => isPortrait && `border: none;`}
`;

const ContactSection = styled.div<{
	hasThreeLinks: boolean;
}>`
	display: flex;
	flex-wrap: wrap;
	${({ hasThreeLinks }) => hasThreeLinks && 'flex-direction: column;'}
	gap: ${Css.pxToRem(8)};
`;

const LinkRow = styled.div`
	display: flex;
	flex-direction: row;
	gap: ${Css.pxToRem(16)};
	align-items: center;
	width: 100%;
	justify-content: flex-start;
	flex-wrap: wrap;
`;

const CardLink = styled(RouterLink)<{}, ITheme>`
	${(props) => ArrowLinkStyles(props.theme)}
	text-decoration: none;
	padding: 0;
`;

const CardPictureWrapper = styled.div<
	{ hasNoImage: boolean; isPortrait?: boolean },
	ITheme
>`
	border-top-left-radius: ${(props) => props.theme.card.borderRadius};
	border-top-right-radius: ${(props) => props.theme.card.borderRadius};
	overflow: hidden;
	${({ isPortrait }) => !isPortrait && `aspect-ratio: 3/1;`}

	${({ isPortrait }) =>
		isPortrait &&
		css`
			padding: ${Css.pxToRem(16)};
			padding-bottom: 0;
			background-color: ${Color.Neutral2};
		`}

	

	${({ hasNoImage }) =>
		hasNoImage &&
		css`
			display: flex;
			align-items: center;
			justify-content: center;
		`}

	svg {
		height: 100%;
		${({ hasNoImage }) => !hasNoImage && 'width: 100%;'}
	}
`;

const CardPictureStyled = styled(StyledCardPicture, {
	shouldForwardProp: (prop) => !['type', 'isPortrait'].includes(prop),
})<
	{
		type: IContactItemProps['type'];
		isPortrait: boolean;
	},
	ITheme
>`
	overflow: hidden;
	border-radius: ${(props) =>
		`${props.theme.card.borderRadius} ${props.theme.card.borderRadius} 0 0`};

	img {
		transition: 0.3s ease-in;
		width: 100%;
		height: 100%;
		object-fit: ${({ type }) => (type === 'contact' ? 'cover' : 'contain')};
		${({ isPortrait }) => isPortrait && `object-position: top;`}
		display: block;

		${({ isPortrait, theme }) =>
			isPortrait &&
			css`
				height: ${PORTRAIT_IMAGE_HEIGHT};
				border-radius: ${theme.card.borderRadius} ${theme.card.borderRadius} 0 0;
			`}

		${({ type }) =>
			type === 'airline' &&
			css`
				padding: ${Css.pxToRem(34)};
				background-color: ${Color.Neutral2};
			`}
	}
`;

const CardContentTitle = styled.h4<{
	hasSubtitle: boolean;
	isPortrait?: boolean;
}>`
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	margin: 0;
	text-overflow: ellipsis;
	${({ isPortrait }) => isPortrait && `font-size: ${Css.pxToRem(24)}`}

	${({ hasSubtitle }) => !hasSubtitle && `margin-bottom: ${Css.pxToRem(22)};`}
`;

const contentStyles = css`
	font-size: ${Css.pxToRem(14)};
	font-weight: 300;
	color: ${Color.Neutral6};
`;

const CardContentSubtitle = styled.div`
	${contentStyles}
	margin-bottom: ${Css.pxToRem(10)};
`;

const CardContentBody = styled.div`
	${contentStyles}
	margin-bottom: ${Css.pxToRem(16)};
`;

const NoImagePortraitWrapper = styled.div`
	height: ${PORTRAIT_IMAGE_HEIGHT};
`;
